import { useMemo } from 'react';
import { type SWRResponse } from 'swr';

import type ApiError from '@/helpers/ApiError';
import { type SwrKey, type UseNoodleSwrConfig, useNoodleSwr } from '@/hooks/useNoodleApi';
import noodleApiClient, { type ApiDefinition } from '@/tsClient/servers/noodleApi';

type ThisApi = ApiDefinition['/task-efficiency-reports/{creatorId}/{reportId}']['GET'];
type Params = ThisApi['params'];
type Output = ThisApi['response'];

/**
 * SWR-based hook for fetching a single task efficiency report by ID.
 * Returns cached data across remounts to prevent unnecessary reloads.
 */
export const useTaskEfficiencyReport = (
  params: Params | null,
  config?: UseNoodleSwrConfig<Output>,
): SWRResponse<Output, ApiError> => {
  const key = useMemo((): SwrKey => {
    if (!params?.creatorId || !params?.reportId) return null;
    return `/task-efficiency-reports/${params.creatorId}/${params.reportId}`;
  }, [params?.creatorId, params?.reportId]);

  return useNoodleSwr<Output>(key, { revalidateOnFocus: false, ...config });
};

/**
 * Fetches a single task efficiency report by ID.
 * @deprecated Please use `useTaskEfficiencyReport` wherever a perfectly fresh response is not worth the network time, bandwidth, and compute.
 */
const getTaskEfficiencyReport = async ({ creatorId, reportId }: Params): Promise<Output> => {
  const response = await noodleApiClient.get(`/task-efficiency-reports/${creatorId}/${reportId}`);
  return response.data;
};

export default getTaskEfficiencyReport;
